import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  WEBHOOK_DIRECTION,
  WEBHOOK_TRANSFER_TYPES_FOR_CONFIG,
  WEBHOOK_ENABLED_STATUSES_OPTIONS,
  WEBHOOK_BATCH_ENABLED_STATUSES_OPTIONS,
  WEBHOOK_STATUSES_METADATA,
  WEBHOOK_BATCH_STATUSES_METADATA
} from 'components/constants';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';

// Transfer type / status rules and batch statuses of ONE webhook.
// Each part of the state is re-initialised only when its saved rules change.
export const WebhookConfiguration = ({ webhook, isLoading, onSave }) => {
  const i18n = useContext(i18nContext);
  // batch events are delivered to Outgoing and Both webhooks only
  const isBatchRuleSupported = webhook?.direction !== WEBHOOK_DIRECTION.INCOMING;

  const [transactionRules, setTransactionRules] = useState({});
  const [batchStatuses, setBatchStatuses] = useState([]);

  // Re-initialise from the server only when the saved rules change, not on every list reload
  // (an URL/secret/direction update must not discard toggles that are not saved yet; switching to
  // Incoming clears the saved batch statuses, which must not reset the transaction toggles)
  const transactionRulesKey = JSON.stringify(webhook?.webhookConfiguration?.paymentTransactionRules || null);
  const batchRuleKey = JSON.stringify(webhook?.webhookConfiguration?.paymentBatchRule || null);

  // Initialize transaction rules from webhook configuration
  useEffect(() => {
    if (webhook?.webhookConfiguration?.paymentTransactionRules) {
      // Convert array from API to object: {NORMAL: ['PAID'], EXCHANGE: ['FAILED']}
      const rulesObject = webhook.webhookConfiguration.paymentTransactionRules.reduce((acc, rule) => {
        acc[rule.transferType] = rule.enabledStatuses || [];
        return acc;
      }, {});
      setTransactionRules(rulesObject);
    } else {
      setTransactionRules({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionRulesKey]);

  // Initialize batch statuses from webhook configuration
  useEffect(() => {
    if (webhook?.webhookConfiguration?.paymentBatchRule?.enabledStatuses) {
      setBatchStatuses(webhook.webhookConfiguration.paymentBatchRule.enabledStatuses);
    } else {
      setBatchStatuses([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRuleKey]);

  const handleToggleStatus = (transferType, status) => {
    setTransactionRules((prev) => {
      const currentStatuses = prev[transferType] || [];
      const isStatusEnabled = currentStatuses.includes(status);

      const updatedStatuses = isStatusEnabled
        ? currentStatuses.filter((s) => s !== status) // Remove status
        : [...currentStatuses, status]; // Add status

      return {
        ...prev,
        [transferType]: updatedStatuses
      };
    });
  };

  const handleToggleBatchStatus = (status) => {
    setBatchStatuses((prev) => {
      const isStatusEnabled = prev.includes(status);

      return isStatusEnabled
        ? prev.filter((s) => s !== status) // Remove status
        : [...prev, status]; // Add status
    });
  };

  const handleSaveConfiguration = async () => {
    // Convert rules object back to array for API
    const paymentTransactionRules = Object.entries(transactionRules)
      .filter(([, enabledStatuses]) => enabledStatuses.length > 0) // Only send rules with selected statuses
      .map(([transferType, enabledStatuses]) => ({
        transferType,
        enabledStatuses
      }));

    await onSave({
      paymentTransactionRules,
      paymentBatchRule: {
        enabledStatuses: isBatchRuleSupported ? batchStatuses : []
      }
    });
  };

  const renderStatusItem = (status, isActive, metadata, messagePrefix, onToggle) => {
    const handleClick = () => !isLoading && onToggle();
    return (
      <div
        key={status}
        className={`webhook-status-item ${isActive ? 'active' : ''}`}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        role={'button'}
        tabIndex={0}
        aria-pressed={isActive}
      >
        <CheckBox
          className={'webhook-status-checkbox'}
          isActive={isActive}
          showCheckBox={true}
          disabled={isLoading}
          onChange={() => {}}
          value={i18n.getMessage(`${messagePrefix}.${status}`)}
        />
        <p className={'webhook-status-description'}>
          {i18n.getMessage(`${messagePrefix}.${status}.description`)}
          {metadata[status]?.isFinal && <span className={'webhook-status-final-badge'}>final</span>}
        </p>
      </div>
    );
  };

  return (
    <div className={'webhook-configuration-section'}>
      <h3 className={'webhook-configuration-title'}>{i18n.getMessage('webhookTab.configuration.title')}</h3>

      {WEBHOOK_TRANSFER_TYPES_FOR_CONFIG.map((transferType) => (
        <div key={transferType} className={'webhook-rule-section'}>
          <div className={'webhook-rule-header'}>
            <h4 className={'webhook-rule-title'}>{i18n.getMessage(`webhookTab.transferType.${transferType}`)}</h4>
            <p className={'webhook-rule-description'}>
              {i18n.getMessage(`webhookTab.transferType.${transferType}.description`)}
            </p>
          </div>
          <div className={'webhook-rule-statuses'}>
            {WEBHOOK_ENABLED_STATUSES_OPTIONS.map((status) =>
              renderStatusItem(
                status,
                transactionRules[transferType]?.includes(status) || false,
                WEBHOOK_STATUSES_METADATA,
                'webhookTab.status',
                () => handleToggleStatus(transferType, status)
              )
            )}
          </div>
        </div>
      ))}

      {/* Batch Webhooks Section */}
      <div className={'webhook-rule-section'}>
        <div className={'webhook-rule-header'}>
          <h4 className={'webhook-rule-title'}>{i18n.getMessage('webhookTab.batchRule.title')}</h4>
          <p className={'webhook-rule-description'}>
            {i18n.getMessage(
              isBatchRuleSupported ? 'webhookTab.batchRule.description' : 'webhookTab.batchRule.notSupported'
            )}
          </p>
        </div>
        {isBatchRuleSupported && (
          <div className={'webhook-rule-statuses'}>
            {WEBHOOK_BATCH_ENABLED_STATUSES_OPTIONS.map((status) =>
              renderStatusItem(
                status,
                batchStatuses.includes(status),
                WEBHOOK_BATCH_STATUSES_METADATA,
                'webhookTab.batchStatus',
                () => handleToggleBatchStatus(status)
              )
            )}
          </div>
        )}
      </div>

      <div className={'webhook-configuration-footer'}>
        <Button
          className={'webhook-save-config-button'}
          roleType={'button'}
          size={'medium'}
          type={'primary'}
          onClick={handleSaveConfiguration}
          isDisabled={isLoading}
        >
          {i18n.getMessage('webhookTab.button.saveConfiguration')}
        </Button>
      </div>
    </div>
  );
};

WebhookConfiguration.propTypes = {
  webhook: PropTypes.shape({
    id: PropTypes.string,
    direction: PropTypes.string,
    webhookConfiguration: PropTypes.shape({
      paymentTransactionRules: PropTypes.arrayOf(
        PropTypes.shape({
          transferType: PropTypes.string,
          enabledStatuses: PropTypes.arrayOf(PropTypes.string)
        })
      ),
      paymentBatchRule: PropTypes.shape({
        enabledStatuses: PropTypes.arrayOf(PropTypes.string)
      })
    })
  }).isRequired,
  isLoading: PropTypes.bool,
  onSave: PropTypes.func.isRequired
};
